import React, { useEffect, Suspense } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { Redirect, Switch } from 'react-router-dom'

// Components
import { FetchRoute } from '../../Components'
import { HOMEOWNER_DIR } from '../App'
import Loading from '../../Components/shared/Loading/LoadingApp'

// Actions
import {
  setCurrentClient,
  fetchClientData,
  fetchClientHomes,
} from '../../actions/client'
import { setCurrentHomeDetail, fetchHomeDetail } from '../../actions/home'
import { fetchBuyerInfoData } from '../../actions/buyerInfo'
import { fetchMortgageRates } from '../../actions/mortgageRates'
import { fetchMarketRecordsDetail } from '../../actions/marketRecords'
import { fetchLatestMortgageRates } from '../../actions/latestMortgageRates'
import { fetchBrandingStats } from '../../actions/brandingStats'
import { fetchHomeSellingInfo } from '../../actions/shouldYouSell'

// Selectors
import { selectClientFetchId } from '../../store/selectors/client'
import { selectHomeFetchId, selectZipcode } from '../../store/selectors/home'
import { selectMarketInterestZipcodes } from '../../store/selectors/buyerInfo'
import { selectSponsorshipId } from '../../store/selectors/sponsorship'

// Constants
import {
  getClientIdFromPath,
  getHomeIdFromPath,
} from '../../constants/navigation'

// Routes
import LazyRoutes from '../LazyRoutes'

const ReportRoutes = ({ match }) => {
  const dispatch = useDispatch()
  const matchedClientId = getClientIdFromPath()
  const matchedHomeId = getHomeIdFromPath()
  const clientFetchId = useSelector(selectClientFetchId)
  const homeFetchId = useSelector(selectHomeFetchId)
  const homeZipcode = useSelector(selectZipcode)
  const sponsorProfileId = useSelector(selectSponsorshipId)
  const clientZipcodes = useSelector(selectMarketInterestZipcodes)

  useEffect(() => {
    if (clientFetchId === 'not-found') {
      window.location = '/not-found'
    }

    if (matchedClientId && matchedClientId !== clientFetchId) {
      dispatch(setCurrentClient(matchedClientId))
    }

    if (matchedHomeId && matchedHomeId !== homeFetchId) {
      dispatch(setCurrentHomeDetail(matchedHomeId))
    }
  }, [clientFetchId, dispatch, homeFetchId, matchedClientId, matchedHomeId])

  useEffect(() => {
    // Initial fetch
    if (clientFetchId) {
      dispatch(fetchClientData(clientFetchId))
      dispatch(fetchMortgageRates())
    }
  }, [clientFetchId, dispatch])

  useEffect(() => {
    // Dispatch client side effects
    if (clientZipcodes) {
      dispatch(fetchMarketRecordsDetail(clientZipcodes))
    }
  }, [clientZipcodes, dispatch])

  useEffect(() => {
    // Dispatch client side effects
    if (clientFetchId) {
      dispatch(fetchClientHomes(clientFetchId))
    }
  }, [clientFetchId, dispatch])

  useEffect(() => {
    if (homeZipcode) {
      dispatch(fetchMarketRecordsDetail([homeZipcode]))

      if (sponsorProfileId) {
        dispatch(
          fetchBrandingStats({
            zip: homeZipcode,
            sponsorProfileId,
          })
        )
      }
    }
  }, [homeZipcode, sponsorProfileId, dispatch])

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <FetchRoute
          path={`${match.url}/${HOMEOWNER_DIR}`}
          fetchId={homeFetchId}
          fetches={fetchId => () => {
            if (fetchId) {
              dispatch(fetchHomeDetail(fetchId))
              dispatch(fetchLatestMortgageRates())
              dispatch(fetchHomeSellingInfo(fetchId))
            }
          }}
          component={LazyRoutes.Homeowner}
        />
        <FetchRoute
          path={`${match.url}`}
          fetchId={clientFetchId}
          fetches={fetchId => () => {
            if (fetchId) {
              dispatch(fetchBuyerInfoData(fetchId))
            }
          }}
          component={LazyRoutes.Buyer}
        />
        <Redirect to='/' />
      </Switch>
    </Suspense>
  )
}

ReportRoutes.propTypes = {
  match: PropTypes.shape({
    url: PropTypes.string,
  }),
}

export default ReportRoutes
